import { useMemo } from "react";
import { useSelector } from "react-redux";

import { WaterFlowPath } from "@nens/deck-gl";

import { getOneDeeData, getSimulationStatus } from "../../../state/simulation";

// *** EXPLANATION ***
// This code gets and updates all the speed and widths of the waterflows.
// It does this by adding the speed and width to all the flows and calculating an average speed for the entire flow.
// We need the average speed so the animation doesn't jump around on a speed update.
// If the simulation is running we use the updateFlowsRunningSimulation to update the time since last update. Taking into account the average speed.
// Then pass that speed and time since last update to the waterflow. This will make sure the animation doesn't jump

let lastUpdateTimestamp = 0;
const lastFlowTime: { [key: string]: number } = {};
const lastAverageSpeedObject: { [key: string]: number } = {};
let savedAverageSpeedAndWidthFlows: WaterFlowPath[][] | null = null;
let timeSinceLastAppliedUpdate = 0;
let lastAppliedUpdateTime = 0;

const useAppliedFlows = (
  flows: WaterFlowPath[][],
  refrenceTimeAnimationProgress: number,
  speedMultiplierPrecentage: number
) => {
  const simulationRunning = useSelector(getSimulationStatus).status === "running";
  const lineData = useSelector(getOneDeeData);

  if (!lastUpdateTimestamp) lastUpdateTimestamp = refrenceTimeAnimationProgress;

  const { speedAndWidthFlows, averageSpeedObject } = useMemo((): {
    speedAndWidthFlows: WaterFlowPath[][];
    averageSpeedObject: { [key: string]: number };
  } => {
    const calculateSpeedAndWidth = (id: number) => {
      if (!lineData) return { speed: 0, width: 0 };
      const { lineIndexOffset, qw, u1 } = lineData;

      const speed = u1[id - lineIndexOffset] || 0;
      const amount = qw[id - lineIndexOffset] || 0;

      return {
        speed: (speed * speedMultiplierPrecentage) / 50,
        width: amount / speed || 0,
      };
    };
    const speedArrayObject: { [key: string]: number[] } = {};
    const speedAndWidthFlows = flows.map((flow) =>
      flow.map((waypoint) => {
        const { averageSpeedKey, maxWidth } = waypoint;
        const { speed, width } = calculateSpeedAndWidth(waypoint.id);

        // Add the speed to the speed array object
        speedArrayObject[averageSpeedKey] = [...(speedArrayObject[averageSpeedKey] || []), speed];

        return { ...waypoint, speed, width: Math.min(maxWidth ?? 999999999, width) };
      })
    );

    // Calculate the average speed for all the averageSpeedKeys
    const averageSpeedObject: { [key: string]: number } = {};
    Object.keys(speedArrayObject).forEach((speedArrayObjectKey) => {
      const speedArray = speedArrayObject[speedArrayObjectKey];
      const averageSpeed = speedArray.reduce((a, b) => a + b, 0) / speedArray.length;

      averageSpeedObject[speedArrayObjectKey] = averageSpeed;
    });

    return {
      speedAndWidthFlows,
      averageSpeedObject,
    };
  }, [speedMultiplierPrecentage, flows, lineData]);

  const updateFlowsRunningSimulation = (): WaterFlowPath[][] => {
    const timeSinceLastUpdate = (new Date().getTime() - lastUpdateTimestamp) / 1000;
    lastUpdateTimestamp = new Date().getTime();

    // Update lastFlowTimeObject for each averageSpeedKey
    Object.keys(averageSpeedObject).forEach((averageSpeedKey) => {
      lastFlowTime[averageSpeedKey] =
        (lastFlowTime[averageSpeedKey] || 0) +
        timeSinceLastUpdate * (lastAverageSpeedObject[averageSpeedKey] || 0);

      lastAverageSpeedObject[averageSpeedKey] = averageSpeedObject[averageSpeedKey];
    });

    // Only update all the flows every 1.5 seconds. Saves alot of calculating for the user
    timeSinceLastAppliedUpdate += timeSinceLastUpdate;
    if (timeSinceLastAppliedUpdate > 1.5 || !savedAverageSpeedAndWidthFlows) {
      timeSinceLastAppliedUpdate = 0;
      lastAppliedUpdateTime = (new Date().getTime() - refrenceTimeAnimationProgress) / 1000;

      // Update the average speed and width flows. Save them outside of the main fucntion to just return the non-updated ones if the update call was to soon
      savedAverageSpeedAndWidthFlows = speedAndWidthFlows.map((flow) =>
        flow.map((waypoint: WaterFlowPath) => {
          const { averageSpeedKey } = waypoint;
          return {
            ...waypoint,
            speed: averageSpeedObject[averageSpeedKey],
            passedTime: lastFlowTime[averageSpeedKey],
          };
        })
      );
    }

    return savedAverageSpeedAndWidthFlows;
  };

  const appliedFlows = !simulationRunning ? speedAndWidthFlows : updateFlowsRunningSimulation();
  return { appliedFlows, lastAppliedUpdateTime };
};

export default useAppliedFlows;
